import {
  createTracker,
  type ContextProvider,
  type NetworkAdapter,
  type StorageAdapter,
  type Tracker,
  type TrackerOptions,
} from '../../core';
import {
  legacyWebtrackPlugin,
  type LegacyWebtrackOptions,
} from '../../protocol/legacy-webtrack';
import type { AppLifecycleHost, AutoPageViewConfig, PageViewAdapter } from './auto-page-view';
import { wireAutoPageView } from './auto-page-view/wire-auto-page-view';
export { wireAutoPageView } from './auto-page-view/wire-auto-page-view';

export interface RuntimeTrackerOptions extends Omit<TrackerOptions, 'storage' | 'network'> {
  storage?: StorageAdapter;
  network?: NetworkAdapter;
  context?: Record<string, any> | ContextProvider;
  legacy?: false | LegacyWebtrackOptions;
  /** 自动 PV 采集开关与配置；默认关闭，未开启的旧项目行为不变。 */
  autoPageView?: boolean | AutoPageViewConfig;
  /** 注入 Taro/Uni 生命周期适配器；开启 autoPageView 时用于自动 PV 采集。 */
  pageViewAdapter?: PageViewAdapter;
}

export function createRuntimeTracker(
  options: RuntimeTrackerOptions,
  defaults: { storage: StorageAdapter; network: NetworkAdapter; appLifecycle?: AppLifecycleHost; deferPageView?: boolean }
): Tracker {
  const {
    storage = defaults.storage,
    network = defaults.network,
    context,
    legacy,
    autoPageView,
    pageViewAdapter,
    ...trackerOptions
  } = options;

  const tracker = createTracker({
    ...trackerOptions,
    storage,
    network,
  });

  if (legacy !== false) {
    tracker.use(legacyWebtrackPlugin({
      ...legacy,
      endpoint: legacy?.endpoint ?? trackerOptions.endpoint,
    }));
  }

  if (typeof context === 'function') {
    tracker.registerContextProvider('runtime-context', context as ContextProvider);
  } else if (context) {
    tracker.setContext(context);
  }

  if (!defaults.deferPageView) wireAutoPageView(tracker, autoPageView, pageViewAdapter, trackerOptions.logLevel, defaults.appLifecycle);

  return tracker;
}